import { ItemShelfType, PlanogramItem } from "@CommonTypes/merchflow/pog/item";
import { PlanogramShelf } from "@CommonTypes/merchflow/pog/shelf";

// Fixtures split into two merchandising groups: products hang on HANGCELL/PEGBOARD
// fixtures and sit on REGULAR/RISER/BASKET fixtures. A product is only incompatible
// with a fixture when the two fall in different groups.
const HANG_SHELF_TYPES: ReadonlyArray<ItemShelfType> = ["HANGCELL", "PEGBOARD"];

const isHangShelfType = (type: ItemShelfType | PlanogramShelf["type"]): boolean =>
  HANG_SHELF_TYPES.includes(type as ItemShelfType);

/** Whether a product's intended shelf type can merchandise on the given fixture type. */
export const areShelfTypesCompatible = (
  itemShelfType: ItemShelfType,
  fixtureType: PlanogramShelf["type"],
): boolean => isHangShelfType(itemShelfType) === isHangShelfType(fixtureType);

/**
 * Products across the given shelves' merged groups that cannot merchandise on `fixtureType`.
 * Merged groups are de-duplicated so a product is never counted twice when several selected
 * shelves belong to the same group. Items whose `shelfType` is unknown (not yet populated by
 * the backend) are treated as compatible, so enforcement stays dormant until real data exists.
 */
export const getIncompatibleItemsForShelvesType = (
  shelves: PlanogramShelf[],
  fixtureType: PlanogramShelf["type"],
): PlanogramItem[] => {
  const visitedShelfIds = new Set<string>();
  const incompatibleItemsByProductCode = new Map<string, PlanogramItem>();

  for (const shelf of shelves) {
    for (const mergedShelf of shelf.getMergedShelves()) {
      if (visitedShelfIds.has(mergedShelf.uniqueId)) continue;
      visitedShelfIds.add(mergedShelf.uniqueId);

      for (const item of mergedShelf.items) {
        if (item.shelfType && !areShelfTypesCompatible(item.shelfType, fixtureType)) {
          incompatibleItemsByProductCode.set(item.productCode, item);
        }
      }
    }
  }

  return [...incompatibleItemsByProductCode.values()];
};
